import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";

import { Divider, Button, Grid, CardContent, Typography, IconButton, Collapse, Box, Stack, Avatar, Paper } from "@mui/material";

import EmailIcon from "@mui/icons-material/Email";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { Feed, Image, Popup, Label, List } from "semantic-ui-react";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import VendorInfoUpdateModal from "./VendorInfoUpdateModal";
import DeleteConfirmationModal from "components/shared/Modal/DeleteConfirmationModal";
import EmailConfirmationModal from "./EmailConfirmationModal";
import Snackbar from "components/shared/Snackbar";
import { SubtitleInline, SectionTitle } from "components/shared/Typography";
import CollapsibleCard, { CollapsibleCardHeader } from "components/shared/CollapsibleCard";
import { updateLogo, getLogo } from "services/appResources";
import SideMenuContainer from "components/shared/SideMenuContainer";
import ActivitiesList from "components/shared/ActivitiesList";

import { useSelector, useDispatch } from "react-redux";
import { vendorState, vendorCertificatesState, vendorActions } from "redux/vendorSlice";
import { loggedInUserState } from "redux/userSlice";
import { onUpdateACORD25SCAN } from "graphql/vendor";

import "./vendor-details.scss";
import { client_mode } from "constants/client_properties";
import { organizationState } from "redux/organizationSlice";
import { clientActions } from "redux/clientSlice";
import { formatPhoneNumber } from "utils/formUtils";
import { useNavPath, getNavigationPath, NAVIGATED_FROM } from "constants/routes";
import { SYSTEM, AGENCY, getVendorLabel, getProjectLabel } from "constants/organization_types";
import StatusIndicatorSelect from "components/shared/StatusIndicatorSelect";
import CertificateTable from "components/CertificateTable";
import ProjectLineItem from "./ProjectLineItem";
import BoxShadow from "components/shared/BoxShadow";
import DocHubSub from "./DocHubSub";
import ActivityTimeline from "./ActivityTimeline";

import useVendorActivities from "hooks/useVendorActivities";
import certificate_actions from "constants/certificate_actions";
import cognito_groups from "constants/cognito_groups";
import AmplitudeService, { AMPLITUDE_EVENT } from "services/amplitude";
import RequestTypeModal from "components/Request/RequestTypeModal";
import DocumentRequestForm from "components/Request/DocumentRequestForm";
import { createDOCUMENTREQUEST, createEMAILNOTIFICATIONS } from "../../graphql/mutations";
import { v4 as uuidv4 } from 'uuid';
import { logVendorActivity } from "services/activity";
import { generateClient } from 'aws-amplify/api';

const apiClient = generateClient();

const vendorStatuses = {
  COMPLIANT: { label: "Compliant", optionLabel: "Compliant" },
  NOT_COMPLIANT: { label: "Not Compliant", optionLabel: "Not Compliant" },
  ARCHIVED: { label: "Archived", optionLabel: "Archive" },
};

const notificationStatuses = {
  DISABLED: { label: "Disabled", optionLabel: "Disabled" },
  ACTIVE: { label: "Active", optionLabel: "Active" },
};

const vendorActivityAction = {
  EDIT: "updated",
};

  // Define side menu items (will be updated with dynamic labels in component)
  const sideMenuItems = [
    { key: "details", label: "Details" },
    { key: "certificates", label: "Certificates" },
    { key: "dochub", label: "DocHub Sub" },
    { key: "requirements", label: "Requirements" },
    { key: "activities", label: "Activities" }
  ];

export default function VendorDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAdmin, loggedInUser } = useSelector(loggedInUserState);
  const { selectedClient, userOrgType } = useSelector(organizationState);
  const clientMode = selectedClient?.mode;
  
  const [editVendor, setEditVendor] = useState(false);
  const [deleteVendor, setDeleteVendor] = useState(false);
  const [emailConfirm, setEmailConfirm] = useState(false);
  const [logo, setLogo] = useState("");
  const [clientLogo, setClientLogo] = useState("");
  const [message, setMessage] = useState();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState(["Custom"]);
  const [emailCertificateId, setEmailCertificateId] = useState();
  const [showActivityDetail, setShowActivityDetail] = useState({ 0: false });
  const [requestTypeModalOpen, setRequestTypeModalOpen] = useState(false);
  const [documentRequestModalOpen, setDocumentRequestModalOpen] = useState(false);
  
  const { vendorId, referrer, referrerId, clientId } = useParams();
  const { vendors } = useSelector(vendorState);
  
  console.log('URL params - clientId:', clientId, 'vendorId:', vendorId);
  console.log('Redux selectedClient:', selectedClient);
  console.log('All vendors:', vendors);
  
  // Try to find vendor by vendorID first, then by id (clientVendor.id)
  let clientVendor = vendors?.find((oneVendor) => oneVendor.vendorID === vendorId);
  if (!clientVendor) {
    // If not found by vendorID, try to find by clientVendor.id
    clientVendor = vendors?.find((oneVendor) => oneVendor.id === vendorId);
  }
  
  console.log('Found clientVendor:', clientVendor);
  
  const vendor = { ...(clientVendor?.vendor || {}) };
  const [vendorActivities, addVendorActivity] = useVendorActivities(clientVendor);
  const hasEditPrivileges = isAdmin || (loggedInUser?.role === cognito_groups.ClientPMRole && loggedInUser?.assignedVendors?.includes(clientVendor?.id));
  const hasAddCertificatePrivileges = hasEditPrivileges || (loggedInUser?.role === cognito_groups.Subcontractor && loggedInUser?.assignedVendors?.includes(clientVendor?.id));
  
  const categories = useSelector((state) => state.client.categories[selectedClient?.clientID]);
  const projects = useSelector((state) => state.client.projects[selectedClient?.clientID]);
  const defaultProject = projects?.find((project) => project.isDefault);
  const vendorCategories = useMemo(() => {
    const vendorCategoriesObject = {};
    if (clientVendor?.categories?.items?.length && categories?.length) {
      clientVendor.categories.items.forEach((vendorCategory) => {
        const category = categories.find((oneCategory) => oneCategory.id === vendorCategory.categoryID);
        if (category) {
          const { id, name, requiredCoverages } = category;
          if (vendorCategory.primary) {
            vendorCategoriesObject.primary = { id, name, requiredCoverages };
          } else {
            vendorCategoriesObject.others = [...(vendorCategoriesObject.others || []), { id, name, requiredCoverages }];
          }
        }
      });
    }
    return vendorCategoriesObject;
  }, [clientVendor?.categories?.items, categories]);
  const vendorProjects = useMemo(() => {
    if (clientVendor?.projects?.items.length && projects?.length) {
      return clientVendor.projects.items.map((vendorProject) => ({
        ...(projects.find((project) => vendorProject.projectID === project.id) || {}),
      }));
    }
  }, [clientVendor?.projects?.items, projects]);

  useEffect(() => {
    if (selectedClient?.clientID) {
      if (clientMode === client_mode.category && !categories) {
        dispatch(clientActions.fetchCategories(selectedClient.clientID));
      } else if (clientMode === client_mode.project && !projects) {
        dispatch(clientActions.fetchProjects(selectedClient.clientID));
      }
    }
  }, [selectedClient?.clientID]);

  const [loadingCertificates, setLoadingCertificates] = useState(false);
  const { vendorCertificates, vendorCertificatesCount } = useSelector(vendorCertificatesState);
  const [certificateLoadTime, setCertificateLoadTime] = useState(Date.now());

  const loadVendorCertificates = useCallback(async () => {
    if (!clientVendor?.id || !selectedClient?.clientID) {
      return;
    }

    console.log('Loading certificates for vendor:', vendor?.name);
    console.log('ClientVendor ID:', clientVendor?.id);
    console.log('Selected client ID:', selectedClient?.clientID);
    console.log('URL client ID:', clientId);

    setLoadingCertificates(true);
    const result = await dispatch(
      vendorActions.getAllVendorClientCertificates({
        clientID: selectedClient.clientID, // Use the Redux selected client ID
        clientVendorID: clientVendor.id,
      })
    );
    console.log('Certificate load result:', result);
    console.log('Certificates payload:', result.payload);
    setCertificateLoadTime(Date.now());
    setLoadingCertificates(false);
  }, [clientVendor?.id, selectedClient?.clientID, dispatch, vendor?.name, clientId]);

  useEffect(() => {
    console.log('VendorDetails useEffect - selectedClient?.clientID:', selectedClient?.clientID, 'clientVendor:', clientVendor);
    
    if (selectedClient?.clientID) {
      if (!clientVendor) {
        console.log('Fetching vendors with clientID:', selectedClient.clientID);
        dispatch(vendorActions.fetchVendors(selectedClient.clientID));
      } else if (selectedClient?.clientID && clientVendor?.id) {
        console.log('About to load certificates');
        loadVendorCertificates();
      }
    }

    if (vendor?.logo) {
      getLogo(vendor.logo, (response) => setLogo(response));
    }

    if (selectedClient?.logo) {
      getLogo(selectedClient.logo, (response) => setClientLogo(response));
    }
  }, [clientVendor?.id, selectedClient?.clientID]);

  // Check if we're returning from certificate creation and refresh the table
  useEffect(() => {
    if (location.state?.fromCertificateCreation && clientVendor?.id && selectedClient?.clientID) {
      console.log('Returning from certificate creation, refreshing certificates...');
      loadVendorCertificates();
      // Clear the state to prevent repeated refreshes
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Subscribe to scan status updates and refresh certificate list when scans complete
  const scanSubscriptionRef = useRef(null);
  const completedScansRef = useRef(new Set());

  useEffect(() => {
    if (!clientVendor?.id || !selectedClient?.clientID) {
      return;
    }

    console.log('Setting up scan status subscription for vendor:', clientVendor.id);

    // Subscribe to all ACORD25SCAN updates
    const subscription = apiClient.graphql({
      query: onUpdateACORD25SCAN
    }).subscribe({
      next: ({ data }) => {
        const scanUpdate = data?.onUpdateACORD25SCAN;
        if (!scanUpdate) return;

        console.log('Received scan update:', {
          scanId: scanUpdate.id,
          certificateID: scanUpdate.certificateID,
          status: scanUpdate.status,
          scanStage: scanUpdate.scanStage
        });

        // When a scan is submitted (completed), refresh the certificate list
        // We'll refresh and let the table show all certificates - no need to filter here
        if (scanUpdate.status === 'SUBMITTED' && !completedScansRef.current.has(scanUpdate.id)) {
          console.log('Scan completed, refreshing certificate list...');
          completedScansRef.current.add(scanUpdate.id);

          // Refresh the certificate list after a short delay to ensure backend processing is complete
          setTimeout(() => {
            loadVendorCertificates();
          }, 1000);
        }
      },
      error: (error) => {
        console.error('Scan subscription error:', error);
      }
    });

    scanSubscriptionRef.current = subscription;

    // Cleanup on unmount
    return () => {
      if (scanSubscriptionRef.current) {
        console.log('Unsubscribing from scan updates');
        scanSubscriptionRef.current.unsubscribe();
        scanSubscriptionRef.current = null;
      }
      completedScansRef.current.clear();
    };
  }, [clientVendor?.id, selectedClient?.clientID, loadVendorCertificates]);

  const NAV_PATH = useNavPath(userOrgType);
  const vendorLabel = getVendorLabel(selectedClient);
  const projectLabel = getProjectLabel(selectedClient);
  
  // Update side menu items with dynamic labels
  // Details tab (index 0) always shows "Details" - no update needed
  sideMenuItems[3].label = projectLabel === 'Coverage' ? 'Coverages' : 'Requirements'; // Requirements tab shows Coverages when project label is Coverage
  
  let referrerTitle = `${vendorLabel}s`;

  if (referrer === NAVIGATED_FROM.PROJECT) {
    referrerTitle = projectLabel;
  }

  const hasDisabledNotifications = clientVendor?.notificationStatus === "DISABLED";

  const projectMap = useMemo(() => {
    const mapToReturn = {};
    if (clientMode === client_mode.project && projects?.length) {
      projects.forEach((project) => {
        mapToReturn[project.id] = project.name;
      });
    }
    return mapToReturn;
  }, [projects, clientMode]);

  const vendorFields = {
    basic: [
      [
        {
          label: "Email",
          field: "email",
          ...(hasEditPrivileges &&
            !hasDisabledNotifications && {
              action: {
                icon: (
                  <Popup position="bottom center" trigger={<EmailIcon />}>
                    {`Email ${vendorLabel}`}
                  </Popup>
                ),
                onClick: () => {
                  setEmailCertificateId(null);
                  setEmailTemplates(["Custom"]);
                  setEmailConfirm(true);
                },
              },
            }),
        },
        {
          label: "Additional Emails",
          field: "additionalEmails",
          ...(hasEditPrivileges &&
            !hasDisabledNotifications && {
              action: {
                icon: (
                  <Popup position="bottom center" trigger={<EmailIcon />}>
                    {`Email ${vendorLabel}`}
                  </Popup>
                ),
                onClick: () => {
                  setEmailCertificateId(null);
                  setEmailTemplates(["Custom"]);
                  setEmailConfirm(true);
                },
              },
            }),
        },
        {
          label: "Phone",
          field: "phone",
          formatter: formatPhoneNumber,
        },
        {
          label: "Entity Name",
          field: "entityName",
          isVisible: vendor?.useEntityName,
        },
      ],
    ],
    additional: [
      { label: "Address 1", field: "address1" },
      { label: "Address 2", field: "address2" },
      [
        { label: "City", field: "city" },
        { label: "State", field: "state" },
        { label: "Zip Code", field: "zip" },
      ],
    ],
    insuranceAgent: [
      [
        { label: "Insurance Agent Name", field: "name", isInsuranceAgentField: true },
        { label: "Insurance Agent Email", field: "email", isInsuranceAgentField: true },
        { label: "Insurance Agent Phone", field: "phone", isInsuranceAgentField: true },
        { label: "Insurance Agent Address 1", field: "address1", isInsuranceAgentField: true },
        { label: "Insurance Agent Address 2", field: "address2", isInsuranceAgentField: true },
        { label: "Insurance Agent City", field: "city", isInsuranceAgentField: true },
        { label: "Insurance Agent State", field: "state", isInsuranceAgentField: true },
        { label: "Insurance Agent Zip Code", field: "zip", isInsuranceAgentField: true },
      ],
    ],
  };

  const certificateColumns = ["selected", "id", "certificateType", "status", "insured", "types", "producer", "certificateDate", "lastModifiedDate", "policyExp", "scan", "actions"];

  const VendorField = ({ label, field, content, action, clientMode, selectedClientMode, formatter, isInsuranceAgentField, isVisible = true }) => {
    const shouldDisplay = isVisible && (!clientMode || !selectedClientMode || clientMode === selectedClientMode);
    const displayContent = (isInsuranceAgentField && vendor.insuranceAgent ? vendor.insuranceAgent[field] : vendor[field]) || content;
    const formattedContent = formatter ? formatter(displayContent) : displayContent?.toString();
    return (
      shouldDisplay && (
        <Grid item xs={12} sm={6} md={4}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.15 }}>
              {label}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {formattedContent || "-"}
              </Typography>
              {action && (content || vendor[field]) && (
                <IconButton size="small" onClick={action.onClick} sx={{ p: 0.25, ml: 0.25 }}>
                  {action.icon}
                </IconButton>
              )}
            </Stack>
          </Box>
        </Grid>
      )
    );
  };

  async function getUploadLink(certificateId, projectID, mode, recipients, requestedRequirements, initialDescriptionOfOperation) {
    if (certificateId === "create") {
      try {
        let initialCoverageRequirements;
        if (projectID) {
          initialCoverageRequirements = projects.find((project) => project.id === projectID)?.requiredCoverages;
        }
        if (initialCoverageRequirements) {
          initialCoverageRequirements = {
            projectID: projectID,
            clientID: selectedClient.clientID,
            coverages: initialCoverageRequirements,
            requestedSupplementalDocumentation: requestedRequirements,
            requestedAt: new Date(),
          };
          initialCoverageRequirements = JSON.stringify(initialCoverageRequirements);
        }
        const result = await dispatch(
          vendorActions.createRequestCertificate({
            clientVendorID: clientVendor.id,
            clientID: selectedClient.clientID,
            sentBy: loggedInUser.email,
            sentTo: recipients,
            ...(projectID ? { projectID } : {}),
            ...(initialCoverageRequirements && { initialCoverageRequirements }),
            ...(initialDescriptionOfOperation && { initialDescriptionOfOperation }),
          })
        );
        
        if (!result.payload || !result.payload.certificateId) {
          throw new Error('Failed to create certificate request');
        }
        
        return window.location.origin + `/vendor/upload/${selectedClient.id}/${vendor?.id}/${result.payload.certificateId}`;
      } catch (error) {
        console.error('Error creating certificate request:', error);
        throw error;
      }
    } else {
      return window.location.origin + `/vendor/upload/${selectedClient.id}/${vendor?.id}/${certificateId}${mode ? `/${mode}` : ""}`;
    }
  }

  function onCancelVendorInfo(setUpdatedLogo) {
    setEditVendor(false);
    setUpdatedLogo(undefined);
  }

  function onCancelDelete() {
    setDeleteVendor(false);
  }

  async function onDeleteVendor() {
    //disable deleting for non-admin users
    if (!isAdmin) {
      setMessage({
        severity: "error",
        text: `You don't have permission to delete ${vendorLabel.toLowerCase()}`,
      });
      setDeleteVendor(false);
      return;
    }
    setDeleting(true);

    //Delete all vendor certificates
    await Promise.all(
      vendorCertificates.map(async (certificate, index) => {
        await dispatch(vendorActions.deleteCertificate(certificate));
        return Promise.resolve();
      })
    );

    const result = await dispatch(vendorActions.deleteVendor(clientVendor));
    if (result?.payload.success) {
      setMessage({
        severity: "success",
        text: `Successfully deleted ${vendorLabel.toLowerCase()}`,
      });
      setTimeout(() => {
        navBack();
      }, 1000);
    } else {
      setMessage({
        severity: "error",
        text: `Unable to delete ${vendorLabel.toLowerCase()}`,
      });
    }
    setDeleting(false);
    setDeleteVendor(false);
  }
  async function onUpdateVendorInfo(vendorData, updatedLogo, setUpdatedLogo) {
    //disable editing for non-admin users
    if (!isAdmin) {
      setMessage({
        severity: "error",
        text: `You don't have permission to update ${vendorLabel.toLowerCase()}`,
      });
      setEditVendor(false);
      return;
    }
    setSaving(true);
    if (updatedLogo) {
      try {
        const fetched = await fetch(updatedLogo.content);
        const blobbed = await fetched.blob();
        await updateLogo(`${vendorData.id}.${updatedLogo.extension}`, blobbed, updatedLogo.type);
      } catch (err) {
        handleLogoUpdateFailed(err, setUpdatedLogo);
        return;
      }
    }
    const input = { ...vendorData };
    updatedLogo && (input.logo = `${vendorData.id}.${updatedLogo.extension}`);

    const result = await dispatch(
      vendorActions.updateVendor({
        ...input,
        clientVendorID: clientVendor.id,
        categories: clientVendor.categories,
        projects: clientVendor.projects,
        defaultProject: projects?.find((project) => project.isDefault),
      })
    );
    if (result?.payload.success) {
      const { categoryUpdateResult, projectsUpdateResult } = result.payload;
      addVendorActivity({
        vendor: vendorData,
        ...(clientMode === client_mode.category ? { categoryUpdateResult, categories } : {}),
        ...(clientMode === client_mode.project && editVendor === client_mode.project ? { projectsUpdateResult, projects } : {}),
      });
      setMessage({
        severity: "success",
        text: `Successfully updated ${vendorLabel.toLowerCase()}`,
      });
      setEditVendor(false);
    } else {
      setMessage({
        severity: "error",
        text: `Unable to update ${vendorLabel.toLowerCase()}`,
      });
    }
    setSaving(false);
  }

  function handleLogoUpdateFailed(err, setUpdatedLogo) {
    setMessage({
      severity: "error",
      text: "Failed to upload new logo",
    });
    setUpdatedLogo(undefined);
  }

  async function onUpdateVendorStatus(status) {
    setSaving(true);
    const result = await dispatch(vendorActions.updateVendorStatus({ id: clientVendor.id, status }));
    if (result?.payload.success) {
      setMessage({
        severity: "success",
        text: `Successfully updated ${vendorLabel.toLowerCase()} status`,
      });
      addVendorActivity({
        vendor: {
          ...clientVendor.vendor,
          status,
          notificationStatus: clientVendor?.notificationStatus,
        },
      });
      AmplitudeService.logEvent(AMPLITUDE_EVENT.SUBCONTRACTOR_STATUS_CHANGED.eventName,
        AMPLITUDE_EVENT.SUBCONTRACTOR_STATUS_CHANGED.eventProperties({
          userEmail: loggedInUser?.email,
          clientId: selectedClient?.id,
          clientName: selectedClient?.name,
          previous: clientVendor?.status,
          next: status,
          subcontractorId: vendorId,
          subcontractorName: vendor?.name
        })
      )
      if (status === vendorStatuses.ARCHIVED.label.toUpperCase()) {
        const result = await dispatch(vendorActions.updateVendorNotificationStatus({ id: clientVendor.id, notificationStatus: "ACTIVE" }));
      }
      setEditVendor(false);
    } else {
      setMessage({
        severity: "error",
        text: `Unable to update ${vendorLabel.toLowerCase()} status`,
      });
    }
    setSaving(false);
  }

  async function onUpdateNotificationsStatus(notificationStatus) {
    setSaving(true);
    const result = await dispatch(vendorActions.updateVendorNotificationStatus({ id: clientVendor.id, notificationStatus }));
    if (result?.payload.success) {
      setMessage({
        severity: "success",
        text: `Successfully updated ${vendorLabel.toLowerCase()} notification status`,
      });
      addVendorActivity({
        vendor: {
          ...clientVendor.vendor,
          status: clientVendor.status,
          notificationStatus,
        },
      });
      setEditVendor(false);
    } else {
      setMessage({
        severity: "error",
        text: `Unable to update ${vendorLabel.toLowerCase()} notification status`,
      });
    }
    setSaving(false);
  }

  function navBack() {
    let navToPath;
    if (referrer === NAVIGATED_FROM.VENDORS) {
      navToPath = getNavigationPath(NAV_PATH.CLIENT_VENDORS, {
        clientId: selectedClient.id,
      });
      navigate(navToPath);
    } else if (referrer === NAVIGATED_FROM.PROJECT) {
      navToPath = getNavigationPath(NAV_PATH.CLIENT_PROJECT_DETAILS, {
        clientId: selectedClient.id,
        projectId: referrerId,
      });
      navigate(navToPath);
    } else {
      const vendorsPath = getNavigationPath(NAV_PATH.CLIENT_VENDORS, { clientId: selectedClient.id });
      navigate(vendorsPath);
    }
  }

  function navToCertificate(id) {
    const certificatePath = getNavigationPath(NAV_PATH.CLIENT_CERTIFICATE_DETAILS, {
      clientId: selectedClient.id,
      vendorId: vendorId,
      certificateId: id,
    });
    navigate(certificatePath, {
      state: {
        referrer: location.pathname,
      },
    });
  }

  function renderVendorFields (fields) {
    return fields.map((field, topIndex) =>
      Array.isArray(field) ? (
        field.map((subField, subIndex) => (
          <VendorField {...subField} selectedClientMode={clientMode} key={`vendor-field-${topIndex}_${subIndex}`} />
        ))
      ) : (
        <VendorField {...field} selectedClientMode={clientMode} key={`vendor-field-${topIndex}`} />
      )
    );
  }

  function onEmailSentResult(success) {
    if (success) {
      setMessage({
        text: "Successfully sent email notification.",
        severity: "success",
      });
    } else {
      setMessage({
        text: "Unable to send email notification.",
        severity: "error",
      });
    }
  }

  function onSendCertificateEmail(certificateId, emailTemplate) {
    setEmailCertificateId(certificateId);
    setEmailTemplates([emailTemplate]);
    setEmailConfirm(true);
  }

  const handleRequestTypeSelect = (type) => {
    if (type === 'CERTIFICATE') {
      // Use existing certificate request flow
      onSendCertificateEmail("create", certificate_actions.REQUEST);
    } else if (type === 'OTHER') {
      // Open document request form
      setDocumentRequestModalOpen(true);
    }
  };

  const handleDocumentRequestSubmit = async (requestData) => {
    console.log('=== DOCUMENT REQUEST SUBMISSION START ===');
    console.log('handleDocumentRequestSubmit called with:', requestData);
    console.log('Request data stringified:', JSON.stringify(requestData, null, 2));
    console.log('clientVendor:', clientVendor);
    console.log('vendor:', vendor);
    console.log('selectedClient:', selectedClient);
    console.log('loggedInUser:', loggedInUser);
    
    try {
      // Validate required data
      if (!clientVendor?.id) {
        throw new Error('Vendor ID is missing');
      }
      if (!selectedClient?.clientID) {
        throw new Error('Client ID is missing');
      }
      if (!loggedInUser?.email) {
        throw new Error('User email is missing');
      }
      
      // Generate unique upload token
      const uploadToken = uuidv4();
      
      // Calculate token expiration (7 days from now)
      const tokenExpiresAt = new Date();
      tokenExpiresAt.setDate(tokenExpiresAt.getDate() + 7);

      // Log the categories structure for debugging
      console.log('Request data categories:', JSON.stringify(requestData.categories, null, 2));

      // Ensure categories have the __typename field if needed
      const formattedCategories = requestData.categories.map(cat => ({
        category: cat.category,
        description: cat.description,
        required: cat.required !== undefined ? cat.required : true
      }));
      
      // Format certificate associations if provided
      const certificateAssociations = requestData.associatedCertificateIds && requestData.associatedCertificateIds.length > 0
        ? {
            certificateIds: requestData.associatedCertificateIds,
            certificates: requestData.certificates?.map(cert => ({
              id: cert.id,
              status: cert.status,
              insuredName: cert.insuredName || cert.insured,
              certificateHolder: cert.certificateHolder,
              expirationDate: cert.firstExp,
              lastModified: cert.updatedAt
            }))
          }
        : null;

      // Create document request - only include fields that are part of the input type
      const input = {
        clientID: clientVendor?.clientID || selectedClient?.clientID,
        clientVendorID: clientVendor?.id,
        projectID: clientVendor?.projectID || null,
        requestType: requestData.requestType,
        categories: formattedCategories,
        requestedBy: `${loggedInUser.firstName} ${loggedInUser.lastName}`,
        requestedByEmail: loggedInUser.email,
        requestedAt: new Date().toISOString(),
        uploadToken,
        tokenUsed: false,
        tokenExpiresAt: tokenExpiresAt.toISOString(),
        status: 'PENDING',
        vendorEmail: clientVendor?.vendorEmail || vendor?.email || clientVendor?.email,
        vendorName: clientVendor?.vendorName || vendor?.name || clientVendor?.entityName,
        clientName: selectedClient?.name || 'Client',
        notes: requestData.notes || '',
        associatedCertificateIds: certificateAssociations?.certificateIds || [],
        certificateDetails: JSON.stringify(certificateAssociations?.certificates || [])
      };

      // Log the full input for debugging
      console.log('Full input being sent to createDOCUMENTREQUEST:', JSON.stringify(input, null, 2));

      const result = await apiClient.graphql({
        query: createDOCUMENTREQUEST,
        variables: { input }
      });

      console.log('Document request created:', result.data.createDOCUMENTREQUEST);

      // Log activity for document request
      if (clientVendor?.id) {
        try {
          let userFullName = loggedInUser?.email || 'Unknown User';
          if (loggedInUser?.firstName || loggedInUser?.lastName) {
            userFullName = `${loggedInUser.firstName || ''}${loggedInUser.firstName ? " " : ""}${loggedInUser.lastName || ''}`.trim();
          }

          await logVendorActivity({
            clientVendorID: clientVendor.id,
            userFullName,
            userID: loggedInUser?.id || 'unknown',
            action: 'REQUEST_DOCUMENT',
            field: JSON.stringify({
              message: 'Document request sent',
              details: {
                documentRequestID: result.data.createDOCUMENTREQUEST.id,
                categories: requestData.categories.map(cat => cat.category),
                recipientEmail: clientVendor?.vendorEmail || vendor?.email
              }
            })
          });

          console.log('Document request activity logged');
        } catch (activityError) {
          console.error('Failed to log document request activity:', activityError);
        }
      }

      // Create email notification to trigger Lambda
      const uploadUrl = `${window.location.origin}/upload/${uploadToken}`;
      const documentRequestId = result.data.createDOCUMENTREQUEST.id;
      
      // Encode document request details in content field as JSON
      // Ensure categories are properly formatted for Lambda
      const emailContent = JSON.stringify({
        documentCategories: requestData.categories.map(cat => ({
          category: cat.category || 'Document',
          description: cat.description || ''
        })),
        documentRequestID: documentRequestId,
        notes: requestData.notes || '',
        associatedCertificateIds: certificateAssociations?.certificateIds || [],
        certificateDetails: certificateAssociations?.certificates || []
      });
      
      const emailInput = {
        recipients: [clientVendor?.vendorEmail || vendor?.email || clientVendor?.email],
        action: 'DOCUMENT_REQUEST',
        vendorName: clientVendor?.vendorName || vendor?.name || clientVendor?.entityName,
        clientName: selectedClient?.name || 'Client',
        uploadLink: uploadUrl,
        subject: 'Document Upload Request - Action Required',
        content: emailContent
        // Removed fields not in schema: id, documentCategories, documentRequestID, notes, createdAt, updatedAt
      };
      
      // Add CC emails if provided
      if (requestData.ccEmails && requestData.ccEmails.length > 0) {
        emailInput.recipients = [...emailInput.recipients, ...requestData.ccEmails];
      }
      
      console.log('Creating email notification with input:', JSON.stringify(emailInput, null, 2));
      
      const emailResult = await apiClient.graphql({
        query: createEMAILNOTIFICATIONS,
        variables: { input: emailInput }
      });
      
      console.log('Email notification created:', emailResult);
      
      // Show success message
      setMessage({
        severity: "success",
        text: "Document request sent successfully"
      });
      
      // Close modal
      setDocumentRequestModalOpen(false);
    } catch (error) {
      console.error('Error sending document request:', error);
      console.error('Error details:', {
        message: error.message,
        errors: error.errors,
        stack: error.stack
      });
      
      // Log the specific GraphQL error
      if (error.errors && error.errors.length > 0) {
        console.error('GraphQL Error:', error.errors[0].message);
        console.error('Full error object:', JSON.stringify(error.errors[0], null, 2));
      }
      
      setMessage({
        severity: "error",
        text: error.errors?.[0]?.message || error.message || "Failed to send document request"
      });
    }
  };


  // Handle hash-based navigation
  useEffect(() => {
    if (!location.hash) {
      navigate(location.pathname + `#/${sideMenuItems[0].key}`);
    }
  }, []);

  const activeItem = String(location.hash).replace("#/", "") || sideMenuItems[0].key;

  return (
    <>
      <Button onClick={navBack}>
        <KeyboardArrowLeftIcon />
        Back to {referrerTitle}
      </Button>
      <Snackbar message={message} setMessage={setMessage} />
      <Snackbar message={hasDisabledNotifications && !message && { text: "Notifications are Inactive", severity: "error", duration: 3600000 }} />
      <VendorInfoUpdateModal
        open={editVendor}
        onClose={onCancelVendorInfo}
        onSave={onUpdateVendorInfo}
        clientMode={clientMode}
        vendor={vendor}
        loading={saving}
        categories={categories}
        projects={projects}
        vendorCategories={clientVendor?.categories}
        vendorProjects={clientVendor?.projects}
        logo={logo}
        vendorLabel={vendorLabel}
        projectLabel={projectLabel}
      />
      <DeleteConfirmationModal
        message={vendor?.name}
        content={`Please be aware that all certificates associated with this ${vendorLabel} will also be removed.`}
        open={deleteVendor}
        onClose={onCancelDelete}
        onDelete={onDeleteVendor}
        loading={deleting}
      />
      <EmailConfirmationModal
        open={!!emailConfirm}
        onClose={() => setEmailConfirm(false)}
        onEmailSentResult={onEmailSentResult}
        vendorName={vendor?.name}
        clientVendorID={clientVendor?.id}
        vendorEmail={vendor?.email}
        clientName={selectedClient?.name}
        clientId={selectedClient?.id}
        clientLogo={clientLogo}
        templateName={emailConfirm}
        clientTemplates={selectedClient?.templates}
        templates={emailTemplates}
        certificateId={emailCertificateId}
        getUploadLink={getUploadLink}
        vendorProjects={clientMode === client_mode.project ? vendorProjects : []}
        defaultProject={defaultProject}
        loggedInUser={loggedInUser}
        useClientDefaultProject={selectedClient?.useDefaultProject}
        additionalRecipients={[...(selectedClient?.usePrimaryNotificationEmailAddress ? [selectedClient?.primaryNotificationEmailAddress] : []), ...(Array.isArray(vendor?.additionalEmails)? vendor?.additionalEmails : [])]}
        projectLabel={projectLabel}
      />
      <Grid container spacing={2} pt={1}>
        <Grid item sm={12} md={12}>
          <Stack direction="row" mb={3} mt={2}>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>
              {vendor?.name}
            </Typography>
            <StatusIndicatorSelect
              currentStatus={clientVendor?.status}
              statuses={vendorStatuses}
              onUpdateStatus={onUpdateVendorStatus}
              sx={{ ml: 2 }}
              size="large"
              disableSelection={!hasEditPrivileges}
            />
            {hasEditPrivileges && (
              <Button
                size={"medium"}
                variant={"contained"}
                sx={{ ml: 2, fontWeight: 600 }}
                onClick={() => setRequestTypeModalOpen(true)}
                disabled={hasDisabledNotifications}
              >
                Request Documents
              </Button>
            )}
          </Stack>
        </Grid>
      </Grid>

      <SideMenuContainer items={sideMenuItems}>
        {/* DETAILS SECTION */}
        {activeItem === sideMenuItems[0].key && (
          <>
            {/* Activity Timeline */}
            <ActivityTimeline
              activities={vendorActivities}
              vendorName={vendor?.name}
            />

            <BoxShadow>
              <Box sx={{ p: 1.5 }}>
                {/* Header with Title and Actions */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {vendorLabel} Information
                  </Typography>
                  {hasEditPrivileges && (
                    <Stack direction="row" spacing={0.5}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => setEditVendor("info")}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => setDeleteVendor(true)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  )}
                </Stack>

                {/* Logo Section */}
                {logo && (
                  <Box sx={{ mb: 0.75, pb: 0.75, borderBottom: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.25 }}>
                      Logo
                    </Typography>
                    <Image src={logo} size="small" />
                  </Box>
                )}

                {/* Contact Information Section */}
                <Box sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: 'primary.main' }}>
                    Contact Information
                  </Typography>
                  <Grid container spacing={1} rowSpacing={0.5}>
                    {renderVendorFields(vendorFields.basic)}
                  </Grid>
                </Box>

                <Divider sx={{ my: 0.75 }} />

                {/* Address Information Section */}
                <Box sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: 'primary.main' }}>
                    Address
                  </Typography>
                  <Grid container spacing={1} rowSpacing={0.5}>
                    {renderVendorFields(vendorFields.additional)}
                  </Grid>
                </Box>

                {/* Insurance Agent Section */}
                {vendor.insuranceAgent && (
                  <>
                    <Divider sx={{ my: 0.75 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: 'primary.main' }}>
                        Insurance Agent
                      </Typography>
                      <Grid container spacing={1} rowSpacing={0.5}>
                        {renderVendorFields(vendorFields.insuranceAgent)}
                      </Grid>
                    </Box>
                  </>
                )}
              </Box>
            </BoxShadow>
          </>
        )}

        {/* CERTIFICATES SECTION */}
        {activeItem === sideMenuItems[1].key && (
          <BoxShadow>
            <SectionTitle sx={{ mb: 2 }}>
              Certificates
              {hasAddCertificatePrivileges && (
                <Popup
                  position="bottom center"
                  trigger={
                    <IconButton sx={{ ml: 2 }} onClick={() => navToCertificate("create")}>
                      <AddCircleIcon color="primary" />
                    </IconButton>
                  }
                >
                  Add Certificate
                </Popup>
              )}
            </SectionTitle>
            {vendorCertificates?.length > 0 && console.log('First certificate supplemental files:', {
              id: vendorCertificates[0].id,
              supplementalCoiFiles: vendorCertificates[0].supplementalCoiFiles,
              files: vendorCertificates[0].files
            })}
            <CertificateTable
              key={"vendorCerTable" + certificateLoadTime}
              loading={loadingCertificates}
              certificates={vendorCertificates || []}
              certificatesCount={vendorCertificatesCount}
              onView={navToCertificate}
              clientMode={clientMode}
              compact={false}
              columns={certificateColumns}
              projects={projectMap}
              onSendEmail={onSendCertificateEmail}
              defaultRows={10}
              hasMultipleSelectionPermissions={isAdmin}
              subcontractors={vendors || []}
            />
          </BoxShadow>
        )}

        {/* DOCHUB SUB SECTION */}
        {activeItem === sideMenuItems[2].key && (
          <BoxShadow>
            <DocHubSub 
              vendor={vendor} 
              clientVendor={clientVendor}
            />
          </BoxShadow>
        )}

        {/* REQUIREMENTS SECTION */}
        {activeItem === sideMenuItems[3].key && (
          <BoxShadow>
              <Grid item sm={12} md={6}>
                {clientMode === client_mode.category && (
                  <Box mb={4}>
                    <SectionTitle sx={{ mb: 2 }}>
                      Categories
                      {hasEditPrivileges && (
                        <Button onClick={() => setEditVendor(client_mode.category)}>
                          {clientVendor?.categories?.items?.length ? "Update" : "Add"}
                        </Button>
                      )}
                    </SectionTitle>
                    {!clientVendor?.categories?.items?.length && (
                      <Typography variant={"subtitle1"} color="text.secondary">
                        This vendor is not assigned to any categories.
                      </Typography>
                    )}
                    {vendorCategories?.primary && (
                      <Box sx={{ display: "flex" }}>
                        <Label>{vendorCategories.primary.name}</Label>
                        <Label basic color="blue" style={{ marginLeft: "auto" }}>
                          primary
                        </Label>
                      </Box>
                    )}
                    {!!vendorCategories?.others?.length && (
                      <>
                        {vendorCategories?.others?.map((otherCategory) => (
                          <Box sx={{ mt: 1 }} key={otherCategory.id}>
                            <Label>{otherCategory.name}</Label>
                          </Box>
                        ))}
                      </>
                    )}
                  </Box>
                )}
                {clientMode === client_mode.project && (
                  <Box mb={4}>
                    <SectionTitle sx={{ mb: 2 }}>
                      {projectLabel}s
                      {hasEditPrivileges && (
                        <Button onClick={() => setEditVendor(client_mode.project)}>
                          Assign
                        </Button>
                      )}
                    </SectionTitle>
                    {!vendorProjects?.length && (
                      <Typography variant={"subtitle1"} color="text.secondary">
                        This {vendorLabel} is not assigned to any {projectLabel}s.
                      </Typography>
                    )}
                    {!!vendorProjects?.length && (
                      <Stack spacing={1}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <SubtitleInline secondary={true}>{`${projectLabel} NAME`.toUpperCase()}</SubtitleInline>
                          <SubtitleInline secondary={true} sx={{ ml: "auto", width: "141px" }}>
                            DURATION
                          </SubtitleInline>
                          <SubtitleInline secondary={true} sx={{ ml: 2, textAlign: "center" }}>
                            STATUS
                          </SubtitleInline>
                        </Box>
                        {vendorProjects.map((project, index) => (
                          <ProjectLineItem key={`project-${index}`} project={project} />
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}
                {hasEditPrivileges && (
                  <Box>
                    <SectionTitle sx={{ mb: 2 }}>Notifications</SectionTitle>
                    <Stack direction="row" alignItems="center">
                      <Typography variant="body1">Status:</Typography>
                      <StatusIndicatorSelect
                        currentStatus={clientVendor?.notificationStatus || "ACTIVE"}
                        statuses={notificationStatuses}
                        onUpdateStatus={onUpdateNotificationsStatus}
                        sx={{ ml: 2 }}
                      />
                    </Stack>
                  </Box>
                )}
              </Grid>
          </BoxShadow>
        )}

        {/* ACTIVITIES SECTION */}
        {activeItem === sideMenuItems[4].key && (
          <ActivitiesList activities={vendorActivities} vendorLabel={vendorLabel} />
        )}
      </SideMenuContainer>
      
      {/* Request Type Selection Modal */}
      <RequestTypeModal
        open={requestTypeModalOpen}
        onClose={() => setRequestTypeModalOpen(false)}
        onSelectType={handleRequestTypeSelect}
      />
      
      {/* Document Request Form Modal */}
      <DocumentRequestForm
        open={documentRequestModalOpen}
        onClose={() => setDocumentRequestModalOpen(false)}
        vendor={{
          name: clientVendor?.vendorName || vendor?.name || clientVendor?.entityName,
          email: clientVendor?.vendorEmail || vendor?.email || clientVendor?.email,
          entityName: clientVendor?.entityName,
          vendorEmail: clientVendor?.vendorEmail || vendor?.email,
          vendorName: clientVendor?.vendorName || vendor?.name
        }}
        certificates={vendorCertificates || []}
        onSubmit={handleDocumentRequestSubmit}
      />
    </>
  );
}
